import { useEffect, useState } from 'react'
import { AlertTriangle, Link2, Loader2, Send, ShieldCheck, Unplug } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/lib/api'

interface ResourceScope {
  mode: 'all_account_resources' | 'restricted'
  domains: Array<{ id: string; name: string }>
  mailboxes: Array<{ id: string; email: string; domain_id: string }>
}

interface RemoteMcpGrant {
  id: string
  client_id: string
  client_name: string
  scopes: string[]
  created_at: string | null
  last_used_at: string | null
  expires_at: string | null
  status: 'active' | 'revoked'
  revoked_at: string | null
  sending_enabled: boolean
  resource_scope: ResourceScope
}

type RevocationTarget = { type: 'one'; grant: RemoteMcpGrant } | { type: 'all' } | null

function timestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : 'Never'
}

function resourceScopeLabel(scope: ResourceScope) {
  if (scope.mode === 'all_account_resources') return 'All account resources'
  const pieces = [
    scope.domains.length ? `${scope.domains.length} domain${scope.domains.length === 1 ? '' : 's'}` : '',
    scope.mailboxes.length ? `${scope.mailboxes.length} mailbox${scope.mailboxes.length === 1 ? '' : 'es'}` : '',
  ].filter(Boolean)
  return pieces.join(' and ') || 'Restricted resources'
}

/** CHECKPOINT: PRD-06 FR-6.5.16 — Owner-visible, confirmed remote-MCP access revocation. */
export function RemoteMcpAccess() {
  const [grants, setGrants] = useState<RemoteMcpGrant[]>([])
  const [loading, setLoading] = useState(true)
  const [revocationTarget, setRevocationTarget] = useState<RevocationTarget>(null)
  const [revoking, setRevoking] = useState(false)
  const [sendingTarget, setSendingTarget] = useState<RemoteMcpGrant | null>(null)
  const [sendingAcknowledged, setSendingAcknowledged] = useState(false)
  const [updatingSending, setUpdatingSending] = useState(false)

  const loadGrants = async () => {
    setLoading(true)
    try {
      const grantsResponse = await api.get('/oauth/grants')
      setGrants(grantsResponse.data.grants || [])
    } catch {
      toast.error('Could not load connected apps')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadGrants()
  }, [])

  const revoke = async () => {
    if (!revocationTarget) return
    setRevoking(true)
    try {
      if (revocationTarget.type === 'all') {
        await api.post('/oauth/grants/revoke-all', { confirmed: true })
        toast.success('All active remote MCP connections were revoked')
      } else {
        await api.post(`/oauth/grants/${revocationTarget.grant.id}/revoke`, { confirmed: true })
        toast.success(`Access for ${revocationTarget.grant.client_name} was revoked`)
      }
      setRevocationTarget(null)
      await loadGrants()
    } catch {
      toast.error('Could not revoke remote MCP access')
    } finally {
      setRevoking(false)
    }
  }

  const setSending = async (grant: RemoteMcpGrant, enabled: boolean) => {
    if (enabled) {
      setSendingAcknowledged(false)
      setSendingTarget(grant)
      return
    }
    setUpdatingSending(true)
    try {
      await api.post(`/oauth/grants/${grant.id}/sending`, { enabled, confirmed: true })
      toast.success(enabled ? `Sending enabled for ${grant.client_name}` : `Sending disabled for ${grant.client_name}`)
      await loadGrants()
    } catch (error: any) {
      toast.error(error?.response?.data?.error_description || 'Could not update sending access')
    } finally {
      setUpdatingSending(false)
    }
  }

  const enableSending = async () => {
    if (!sendingTarget || !sendingAcknowledged) return
    setUpdatingSending(true)
    try {
      await api.post(`/oauth/grants/${sendingTarget.id}/sending`, { enabled: true, confirmed: true })
      toast.success(`Sending enabled for ${sendingTarget.client_name}`)
      setSendingTarget(null)
      await loadGrants()
    } catch (error: any) {
      toast.error(error?.response?.data?.error_description || 'Could not enable sending access')
    } finally {
      setUpdatingSending(false)
    }
  }

  const activeGrants = grants.filter((grant) => grant.status === 'active')

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              Connected apps
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Review connected apps and their access.
            </p>
          </div>
          {activeGrants.length > 1 && (
            <Button variant="outline" size="sm" onClick={() => setRevocationTarget({ type: 'all' })}>
              Revoke all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          Remote MCP never receives your PurpleToad password. Revoking access immediately blocks the app and invalidates its refresh tokens.
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading connected apps
          </div>
        ) : grants.length === 0 ? (
          <div className="rounded-md border border-dashed p-5 text-center">
            <ShieldCheck className="mx-auto h-5 w-5 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">No connected apps</p>
            <p className="mt-1 text-xs text-muted-foreground">Approved apps appear here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {grants.map((grant) => (
              <div key={grant.id} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{grant.client_name}</p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{grant.client_id}</p>
                  </div>
                  {grant.status === 'active' ? (
                    <Button variant="outline" size="sm" onClick={() => setRevocationTarget({ type: 'one', grant })}>
                      <Unplug className="mr-1.5 h-3.5 w-3.5" /> Revoke
                    </Button>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">Revoked</span>
                  )}
                </div>
                <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                  <p><span className="text-foreground">Access:</span> {resourceScopeLabel(grant.resource_scope)}</p>
                  <p><span className="text-foreground">Last used:</span> {timestamp(grant.last_used_at)}</p>
                  <p className="sm:col-span-2"><span className="text-foreground">Scopes:</span> {grant.scopes.join(', ')}</p>
                </div>
                {grant.scopes.includes('mail.send') && grant.status === 'active' && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 p-3">
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-medium"><Send className="h-3.5 w-3.5" />Send email</p>
                      <p className="mt-1 text-xs text-muted-foreground">{grant.sending_enabled ? 'This app can send, reply, forward, and schedule email automatically from its allowed mailboxes. Compatible AI clients may still show their own confirmation.' : 'Disabled. This app cannot send email through PurpleToad Mail.'}</p>
                    </div>
                    <Button variant={grant.sending_enabled ? 'outline' : 'default'} size="sm" disabled={updatingSending} onClick={() => void setSending(grant, !grant.sending_enabled)}>
                      {grant.sending_enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={revocationTarget !== null} onOpenChange={(open) => !open && !revoking && setRevocationTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{revocationTarget?.type === 'all' ? 'Revoke all remote MCP connections?' : 'Revoke remote MCP connection?'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {revocationTarget?.type === 'all'
              ? 'Every active connected app will lose access immediately. Each app will need your approval again to reconnect.'
              : `This will immediately block ${revocationTarget?.type === 'one' ? revocationTarget.grant.client_name : 'this app'}. It will need your approval again to reconnect.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevocationTarget(null)} disabled={revoking}>Cancel</Button>
            <Button variant="destructive" onClick={() => void revoke()} disabled={revoking}>
              {revoking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Revoke access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendingTarget !== null} onOpenChange={(open) => !open && !updatingSending && setSendingTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allow this connected app to send email?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm font-medium">This enables automation, not a per-email PurpleToad Mail review.</p>
            </div>
            <p>
              When enabled, <span className="font-medium text-foreground">{sendingTarget?.client_name}</span> can send, reply to, forward, and schedule email from the mailboxes you selected. PurpleToad Mail will not ask you to approve each individual message.
            </p>
            <p>
              The app could send to the wrong recipient, include incorrect information, misunderstand a request, or be influenced by misleading content in an email or prompt. If someone gains access to this connected AI client, it can send email until you disable or revoke its access.
            </p>
            <p>
              PurpleToad Mail still applies this app’s account, domain, and mailbox limits, along with normal sending limits and abuse protections. You can disable sending or revoke this app at any time.
            </p>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-foreground">
              <Checkbox checked={sendingAcknowledged} onCheckedChange={(checked) => setSendingAcknowledged(checked === true)} />
              <span className="text-sm">I understand this connected app may send email automatically from my selected mailboxes.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendingTarget(null)} disabled={updatingSending}>Cancel</Button>
            <Button onClick={() => void enableSending()} disabled={!sendingAcknowledged || updatingSending}>
              {updatingSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enable sending
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
